/**
 * Service Worker Registration
 * 
 * Registers and manages the service worker for PWA functionality
 */

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function register(config) {
  if ('serviceWorker' in navigator) {
    // Wait for the page to load
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // Running on localhost - check if service worker exists
        checkValidServiceWorker(swUrl, config);

        navigator.serviceWorker.ready.then(() => {
          console.log(
            '[PWA] App is running from cache by a service worker. ' +
            'Learn more: https://cra.link/PWA'
          );
        });
      } else {
        // Not localhost - register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.log('[PWA] Service Worker registered successfully:', registration.scope);

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content available - notify user
              console.log('[PWA] New content available! Please refresh.');

              // Execute callback if provided
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Content is cached for offline use
              console.log('[PWA] Content cached for offline use.');

              // Execute callback if provided
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('[PWA] Service Worker registration failed:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if service worker file exists
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // Service worker not found - reload page
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found - proceed with registration
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[PWA] No internet connection. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        console.log('[PWA] Service Worker unregistered');
      })
      .catch((error) => {
        console.error('[PWA] Error unregistering Service Worker:', error);
      });
  }
}

// Check for updates periodically (every hour)
export function checkForUpdates() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.update();
    });
  }
}

// Request notification permission
export function requestNotificationPermission() {
  if ('Notification' in window && 'serviceWorker' in navigator) {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        console.log('[PWA] Notification permission granted');
      } else {
        console.log('[PWA] Notification permission denied');
      }
    });
  }
}

// Show install prompt for PWA
let deferredPrompt = null;
const PWA_INSTALLED_STORAGE_KEY = 'pwa-installed';

function markPwaInstalled() {
  try {
    window.localStorage.setItem(PWA_INSTALLED_STORAGE_KEY, 'true');
  } catch (error) {
    // Ignore storage failures (private mode, quota, etc.)
  }
}

function wasPwaInstalledBefore() {
  try {
    return window.localStorage.getItem(PWA_INSTALLED_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

window.addEventListener('beforeinstallprompt', (e) => {
  // Prevent the mini-infobar from appearing on mobile
  e.preventDefault();
  // Store the event for later use
  deferredPrompt = e;
  window.dispatchEvent(new Event('pwa:installable'));
  console.log('[PWA] Install prompt ready');
});

window.addEventListener('appinstalled', () => {
  deferredPrompt = null;
  markPwaInstalled();
  window.dispatchEvent(new Event('pwa:installed'));
});

export function showInstallPrompt() {
  if (!deferredPrompt) {
    return Promise.resolve({ status: 'not-available' });
  }

  const promptEvent = deferredPrompt;
  deferredPrompt = null;
  window.dispatchEvent(new Event('pwa:installable-changed'));

  return promptEvent
    .prompt()
    .then(() => promptEvent.userChoice)
    .then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('[PWA] User accepted the install prompt');
        markPwaInstalled();
      } else {
        console.log('[PWA] User dismissed the install prompt');
      }

      return { status: choiceResult.outcome };
    })
    .catch((error) => {
      console.error('[PWA] Failed to show install prompt:', error);
      return { status: 'error', error };
    });
}

export function isInstallable() {
  return deferredPrompt !== null;
}

// Detect if app is running in standalone mode
export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    window.matchMedia('(display-mode: window-controls-overlay)').matches ||
    window.navigator.standalone === true
  );
}

export function isLikelyInstalled() {
  return isStandalone() || wasPwaInstalledBefore();
}

const serviceWorkerConfig = {
  register,
  unregister,
  checkForUpdates,
  requestNotificationPermission,
  showInstallPrompt,
  isInstallable,
  isStandalone,
  isLikelyInstalled,
};

export default serviceWorkerConfig;
